<template>
  <div class="h-screen overflow-y-scroll bg-white">
    <div class="fixed w-full bg-white z-20 h-12">
      <div class="flex py-3 mx-auto" style="width: 960px">
        <button class="outline-none" @click="close()">
          <img src="@/assets/icons/Exit.svg" />
        </button>
        <span
          class="text-base text-gray-92 font-semibold"
          style="margin-left: 380px"
          >{{ state.declineData.seller_company_name }}</span
        >
      </div>
    </div>
    <div class="bg-white pt-24">
      <Form
        v-slot="{ handleSubmit, errors }"
        :validation-schema="state.validationSchema"
        as="div"
      >
        <form @submit="handleSubmit($event, onSubmit)">
          <section class="mx-auto" style="width: 640px">
            <CompTxDeclineFormParts
              v-for="form in state.formData"
              :key="form.id"
              :form="form"
              :rules="form.input_validation_rule || ''"
              :errors="errors"
              :validateFlg="state.validateFlg"
              :hiddenItems="state.hiddenItems"
              v-model:inputValue="state.inputData"
              @reset-hidden-item-input="resetHiddenItemInput"
            ></CompTxDeclineFormParts>
          </section>

          <div class="text-center py-6">
            <Field
              as="input"
              type="checkbox"
              name="consent"
              id="consent"
              class="mr-3"
              value="1"
              v-model="state.consent"
            />
            <label for="consent" class="text-gray-92">内容を確認しました</label>
            <div class="text-red-600"></div>
          </div>

          <button
            type="submit"
            class="block mt-3 mb-9 mx-auto text-white font-semibold rounded focus:outline-none"
            style="width: 200px; height: 40px"
            :class="[
              state.consent && !Object.keys(errors).length
                ? 'bg-secondary-main'
                : 'bg-slate-300',
            ]"
            :disabled="
              !state.consent || Object.keys(errors).length || state.isSubmitted
            "
          >
            この内容で送信
          </button>

          <div class="text-center" style="padding: 36px">
            <span @click="close()" class="border-b text-gray-62 cursor-pointer"
              >辞退を取りやめる</span
            >
          </div>
        </form>
      </Form>
    </div>
  </div>
</template>

<script>
import { reactive, computed, onMounted, inject } from 'vue';
import { Field, Form } from 'vee-validate';
import { useRouter } from 'vue-router';
import axios from '@/utils/axiosAuth.js';
import utils from '@/utils/utils.js';
import rules from '@/utils/validateRules.js';
import CompTxDeclineFormParts from '@/views/tx/vue/parts/CompTxDeclineFormParts.vue';

export default {
  props: {
    id: Number,
    selectId: Number,
    txType: String,
  },
  components: {
    Form,
    Field,
    CompTxDeclineFormParts,
  },
  setup(props, { emit }) {
    const router = useRouter();
    const globalMethods = inject('globalMethods');
    const state = reactive({
      isSubmitted: false,
      isDraftedDisplay: 1,
      formData: {},
      inputData: {},
      declineData: {},
      validationSchema: {},
      consent: false,
      hiddenItems: computed(() => {
        return utils.checkHiddenItems(state.formData, state.inputData.items);
      }),
    });

    onMounted(async () => {
      await axios
        .get('/sellers')
        .then((response) => {
          state.isDraftedDisplay = response.data.data[0].is_drafted_display;
        })
        .catch((e) => {
          globalMethods.updateModal(
            true,
            'リクエストが完了しませんでした。',
            e.systemMessage || '',
            e.callback
          );
          globalMethods.sendLog(e);
        });

      let path =
        props.txType == 'tx'
          ? `/transactions/${props.id}/est-refuse`
          : `/transaction-selects/${props.selectId}/select-refuse`;
      axios
        .get(path)
        .then((response) => {
          if (response.data.is_refused === 1) {
            close();
            globalMethods.isToast('既に辞退済みです。', { timeout: 3000 });
          }
          Object.assign(state.declineData, response.data);
          makeFormData(response.data.items);
          makeInputData(response.data.items);
          makeValidationSchema();
        })
        .catch((e) => {
          globalMethods.updateModal(
            true,
            'URLが正しくないか、通信エラーが発生しました。',
            e.systemMessage || '',
            e.callback
          );
          globalMethods.sendLog(e);
        });

      function makeFormData(response) {
        Object.assign(state.formData, { ...response });
      }
      function makeInputData(response) {
        const items = utils.array2Object(response);
        Object.assign(state.inputData, { items: utils.object2Array(items) });
      }
      function makeValidationSchema() {
        let formDataArray = [];
        Object.keys(state.formData).forEach((key) => {
          formDataArray.push(state.formData[key]);
        });
        for (let i = 0; i < formDataArray.length; i++) {
          if (formDataArray[i].input_validation_rule != null) {
            state.validationSchema['itemId' + formDataArray[i].id] = (
              value
            ) => {
              return rules.validate(
                value,
                formDataArray[i].input_validation_rule
              );
            };
          }
        }
      }
    });

    const resetHiddenItemInput = () => {
      for (const [key, val] of Object.entries(state.hiddenItems)) {
        if (!val) {
          let target;
          for (const form of Object.values(state.formData)) {
            if (form.id == key) {
              target = form;
              break;
            }
          }
          for (const [i, e] of state.inputData.items.entries()) {
            if (e.id == target.id) {
              if (target.input_type === 'checkbox') {
                state.inputData.items[i].values = [];
              } else {
                state.inputData.items[i].values = '';
              }
              break;
            }
          }
        }
      }
    };

    const close = () => {
      emit('close');
    };

    const onSubmit = () => {
      if (state.isDraftedDisplay) {
        return;
      }

      state.isSubmitted = true;
      const filteredItems = utils.excludeHiddenInput(
        state.inputData.items,
        state.hiddenItems
      );
      const postData = {
        id: state.declineData.id,
        transaction_service_id: state.declineData.transaction_service_id,
        total_with_tax: state.declineData.total_with_tax,
        is_expired: state.declineData.is_expired,
        expired_at: state.declineData.expired_at,
        is_refused: state.declineData.is_refused,
        is_published: state.declineData.is_published,
        published_at: state.declineData.published_at,
        transaction_id: state.declineData.transaction_id,
        tax_rate: state.declineData.tax_rate,
        currency_code: state.declineData.currency_code,
        transaction_published_at: state.declineData.transaction_published_at,
        buyer_company_name: state.declineData.buyer_company_name,
        category_id: state.declineData.category_id,
        category_name: state.declineData.category_name,
        currency_symbol: state.declineData.currency_symbol,
        items: state.formData,
      };

      for (const key of Object.keys(state.formData)) {
        state.formData[key].values = filteredItems.find(
          (x) => x.id == state.formData[key].id
        ).values;
      }

      let path =
        props.txType == 'tx'
          ? `transactions/${props.id}/est-refuse/publish`
          : `transaction-selects/${props.selectId}/select-refuse/publish`;
      axios
        .post(path, postData)
        .then(() => {
          if (props.txType == 'tx') {
            globalMethods.isToast('見積もりを辞退しました。', {
              timeout: 3000,
            });
            // Refresh page
            router.go(0);
          } else {
            globalMethods.isToast('辞退しました。', { timeout: 3000 });
            // Refresh page
            router.go(0);
            window.scrollTo(0, 0);
          }
        })
        .catch((e) => {
          globalMethods.updateModal(
            true,
            'リクエストが完了しませんでした。',
            e.systemMessage || '',
            e.callback
          );
          globalMethods.sendLog(e);
        });
    };

    return {
      state,
      onMounted,
      onSubmit,
      close,
      resetHiddenItemInput,
    };
  },
};
</script>

<style lang="scss" scoped>
input[type='checkbox'] {
  box-sizing: border-box;
  display: inline-block;
  position: relative;
  border: 1px solid transparent;
  border-radius: 3px;
  height: 14px;
  &::before {
    position: absolute;
    background: #fff;
    content: '';
    display: block;
    width: 16px;
    height: 16px;
    @apply border;
    border-radius: 3px;
  }
  &::after {
    position: absolute;
    top: 2px;
    left: 6px;
    border-right: 2px solid #fff;
    border-bottom: 2px solid #fff;
    content: '';
    display: block;
    width: 5px;
    height: 9px;
    opacity: 0;
    transform: rotate(48deg);
  }
  &:hover::before {
    @apply border border-gray-92;
    box-shadow: 0px 0px 0px 2px rgba(52, 68, 100, 0.1);
  }
  &:checked::before {
    background: #28395c;
    border: 1px solid #28395c;
  }
  &:checked::after {
    opacity: 1;
  }
}
button:hover {
  opacity: 0.7;
}
</style>
